import { useCallback, useEffect, useState, useMemo } from 'react';
import { formatDate, parseDate, formatForInput } from '../../utils/dateUtils';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ClinicalDataCard from './ClinicalDataCard';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import PlatformService from '../../services/PlatformService';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { fetchVisits, createVisit, updateVisit, deleteVisit } from '../../services/visitService';

const validationSchema = Yup.object({
  visit_date: Yup.date().required('Required'),
  chief_complaint: Yup.string(),
  diagnosis: Yup.string(),
  treatment_plan: Yup.string(),
  notes: Yup.string(),
  follow_up: Yup.string().test(
    'follow-up-after-visit',
    'Follow-up date must be after the visit date',
    function (value) {
      if (!value) {
        return true;
      }

      const visitDateValue = this.parent.visit_date;
      const visitDate = parseDate(visitDateValue);
      const followUpDate = parseDate(value);
      if (!visitDate || !followUpDate) {
        return true;
      }

      return followUpDate > visitDate;
    }
  ),
});

const resolveVisitId = (visit) => visit?.id ?? visit?.id_vs ?? visit?.ID_VS ?? null;

const formatVisitField = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }
  return String(value);
};

const formatVisitDate = (value) => formatDate(value);

const toDateInputValue = (value) => formatForInput(value);

const normalizeVisitRecord = (visit, fallbackPatientId) => {
  if (!visit) {
    return visit;
  }

  const normalized = {
    ...visit,
    id_pt: visit.id_pt ?? visit.id ?? fallbackPatientId ?? null,
    visit_date: visit.visit_date ?? visit.date ?? null,
    follow_up: visit.follow_up ?? visit.follow_up_date ?? null,
  };

  return normalized;
};

/**
 * Professional Clinical Report View for Medical Visits.
 * Provides a high-fidelity, printable summary of a clinical encounter.
 */
const VisitReportView = ({ visit, patientName, age, gender, amka, dateOfBirth, phone, address, diagnosis }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  if (!visit) return null;

  const chiefComplaint = visit.chief_complaint ?? visit.provlima;
  const visitDiagnosis = visit.diagnosis ?? visit.eurimata;
  const treatmentPlan = visit.treatment_plan ?? visit.plano;
  const notes = visit.notes ?? visit.my_notes;

  return (
    <Box sx={{ p: 4, bgcolor: 'background.paper', color: 'text.primary', minHeight: '297mm', '@media print': { bgcolor: 'white', color: 'black' } }}>
      {/* Hospital-Grade Medical Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #1976d2', pb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 0.5 }}>
            MEDICAL VISIT REPORT
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#666', fontWeight: 500 }}>
            MySurgeon EMR - Clinical Documentation System
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Date: {formatVisitDate(new Date())}</Typography>
          <Typography variant="body2">Ref: VIS-{resolveVisitId(visit) || 'NEW'}</Typography>
        </Box>
      </Box>

      {/* Patient Identity Card */}
      <Paper variant="outlined" sx={{ mb: 4, p: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', color: '#495057', letterSpacing: 1 }}>
          Patient Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>FULL NAMES</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{patientName || '—'}</Typography>
          </Grid>
          <Grid item xs={3} md={1.5}>
            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>AGE</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{age || '—'}</Typography>
          </Grid>
          <Grid item xs={3} md={1.5}>
            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>GENDER</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{gender || '—'}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>AMKA</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{amka || '—'}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>DOB</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatVisitDate(dateOfBirth)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Clinical Encounter Details */}
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, borderBottom: '1px solid #eee', mb: 1, pb: 0.5 }}>
            ENCOUNTER DATE
          </Typography>
          <Typography variant="body1">{formatVisitDate(visit.visit_date)}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, borderBottom: '1px solid #eee', mb: 1, pb: 0.5 }}>
            CHIEF COMPLAINT
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{chiefComplaint || 'No complaints recorded.'}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, borderBottom: '1px solid #eee', mb: 1, pb: 0.5 }}>
            DIAGNOSIS / CLINICAL FINDINGS
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{visitDiagnosis || 'No clinical findings recorded.'}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, borderBottom: '1px solid #eee', mb: 1, pb: 0.5 }}>
            TREATMENT PLAN
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{treatmentPlan || 'No treatment plan recorded.'}</Typography>
        </Box>

        {notes && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, borderBottom: '1px solid #eee', mb: 1, pb: 0.5 }}>
              ADDITIONAL NOTES
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{notes}</Typography>
          </Box>
        )}

        {visit.follow_up && (
          <Box sx={{ mt: 2, p: 2, bgcolor: isDarkMode ? 'primary.dark' : '#fff9c4', color: isDarkMode ? 'white' : 'inherit', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              FOLLOW-UP APPOINTMENT: {formatVisitDate(visit.follow_up)}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Clinical Footer & Digital Signature */}
      <Box sx={{ mt: 'auto', pt: 8 }}>
        <Grid container justifyContent="flex-end">
          <Grid item xs={5} sx={{ textAlign: 'center' }}>
            <Divider sx={{ mb: 1, borderColor: '#000' }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Attending Physician</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>Digital Record Synchronized via MySurgeon EMR</Typography>
          </Grid>
        </Grid>
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Page 1 of 1 — Generated on {new Date().toLocaleString('en-GB')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const VisitsTab = ({ patientId, apiClient, patientName = '', age, gender, dateOfBirth, amka, phone, address, diagnosis }) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('visit_date');
  const [previewVisit, setPreviewVisit] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('clinicalViewMode') || 'card');

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem('clinicalViewMode', newMode);
    }
  };

  const loadVisits = useCallback(async () => {
    if (!apiClient || !patientId) return;

    setLoading(true);
    setError('');

    try {
      const data = await fetchVisits(apiClient, patientId);
      const normalized = Array.isArray(data)
        ? data.map((item) => normalizeVisitRecord(item, patientId))
        : [];
      setVisits(normalized);
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, [apiClient, patientId]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const handleAdd = () => {
    setEditingVisit(null);
    setDialogOpen(true);
  };

  const handleEdit = (visit) => {
    setEditingVisit(normalizeVisitRecord(visit, patientId));
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visit?')) return;

    try {
      await deleteVisit(apiClient, id);
      setVisits((prev) => prev.filter((v) => resolveVisitId(v) !== id));
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to delete visit');
    }
  };

  const handlePreview = (visit) => {
    setPreviewVisit(normalizeVisitRecord(visit, patientId));
  };

  const handlePrint = (visit) => {
    const reportElement = (
      <VisitReportView 
        visit={visit} 
        patientName={patientName}
        age={age}
        gender={gender}
        amka={amka}
        dateOfBirth={dateOfBirth}
        phone={phone}
        address={address}
        diagnosis={diagnosis}
      />
    );
    PlatformService.print(reportElement, `Visit_Report_${patientName.replace(/\s+/g, '_')}_${formatVisitDate(visit.visit_date)}`);
  };

  const handleSubmit = async (values, { setStatus }) => {
    setSubmitting(true);
    setStatus(undefined);

    try {
      const payload = {
        id_pt: patientId,
        visit_date: values.visit_date,
        date: values.visit_date,
        chief_complaint: values.chief_complaint || null,
        provlima: values.chief_complaint || null,
        diagnosis: values.diagnosis || null,
        eurimata: values.diagnosis || null,
        treatment_plan: values.treatment_plan || null,
        plano: values.treatment_plan || null,
        notes: values.notes || null,
        my_notes: values.notes || null,
        follow_up: values.follow_up || null,
      };

      if (editingVisit) {
        const visitId = resolveVisitId(editingVisit);
        const updated = normalizeVisitRecord(await updateVisit(apiClient, visitId, payload), patientId);
        const updatedId = resolveVisitId(updated) ?? visitId;
        setVisits((prev) => prev.map((v) => (resolveVisitId(v) === updatedId ? updated : v)));
      } else {
        const created = normalizeVisitRecord(await createVisit(apiClient, payload), patientId);
        setVisits((prev) => [created, ...prev]);
      }

      setDialogOpen(false);
    } catch (err) {
      setStatus(err.response?.data?.error ?? err.message ?? 'Failed to save visit');
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = editingVisit
    ? {
        visit_date: toDateInputValue(editingVisit.visit_date ?? editingVisit.date),
        chief_complaint: editingVisit.chief_complaint ?? editingVisit.provlima ?? '',
        diagnosis: editingVisit.diagnosis ?? editingVisit.eurimata ?? '',
        treatment_plan: editingVisit.treatment_plan ?? editingVisit.plano ?? '',
        notes: editingVisit.notes ?? editingVisit.my_notes ?? '',
        follow_up: toDateInputValue(editingVisit.follow_up),
      }
    : {
        visit_date: new Date().toISOString().split('T')[0],
        chief_complaint: '',
        diagnosis: '',
        treatment_plan: '',
        notes: '',
        follow_up: '',
      };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedVisits = useMemo(() => {
    return [...visits].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to string for comparison if not already
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Handle date fields
      if (orderBy.includes('date') || orderBy.includes('follow_up')) {
        aValue = parseDate(aValue)?.getTime() || 0;
        bValue = parseDate(bValue)?.getTime() || 0;
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [visits, order, orderBy]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Visits History</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select
              labelId="sort-by-label"
              value={orderBy}
              label="Sort By"
              onChange={(e) => setOrderBy(e.target.value)}
            >
              <MenuItem value="visit_date">Visit Date</MenuItem>
              <MenuItem value="chief_complaint">Complaint</MenuItem>
              <MenuItem value="diagnosis">Diagnosis</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={`Sort ${order === 'asc' ? 'Descending' : 'Ascending'}`}>
            <IconButton onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')} color="primary">
              <TableSortLabel active direction={order} hideSortIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh visits">
            <IconButton onClick={loadVisits} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            color="primary"
          >
            <ToggleButton value="card" aria-label="card view">
              <Tooltip title="Card View">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table View">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Visit
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {visits.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No visits recorded yet
          </Typography>
        </Paper>
      ) : viewMode === 'card' ? (
        <Grid container spacing={2}>
          {sortedVisits.map((visit, index) => {
            const visitId = resolveVisitId(visit) ?? index;
            const visitDate = formatVisitDate(visit.visit_date);
            const chiefComplaint = (visit.chief_complaint ?? visit.provlima) || 'Routine Visit';
            const diagnosis = visit.diagnosis ?? visit.eurimata;
            const treatmentPlan = visit.treatment_plan ?? visit.plano;
            const followUp = formatVisitDate(visit.follow_up);

            return (
              <Grid item xs={12} sm={6} md={4} key={visitId}>
                <ClinicalDataCard
                  title={chiefComplaint}
                  date={visitDate}
                  type="visit"
                  content={[
                    { label: 'Diagnosis', value: diagnosis },
                    { label: 'Treatment Plan', value: treatmentPlan },
                    { label: 'Follow-up', value: followUp !== '—' ? followUp : null },
                  ]}
                  onEdit={() => handleEdit(visit)}
                  onDelete={() => handleDelete(visitId)}
                  onPreview={() => handlePreview(visit)}
                  onPrint={() => handlePrint(visit)}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', py: 1.5 }}>
                  <TableSortLabel
                    active={orderBy === 'visit_date'}
                    direction={orderBy === 'visit_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('visit_date')}
                  >
                    DATE
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>
                  <TableSortLabel
                    active={orderBy === 'chief_complaint'}
                    direction={orderBy === 'chief_complaint' ? order : 'asc'}
                    onClick={() => handleRequestSort('chief_complaint')}
                  >
                    COMPLAINT
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>DIAGNOSIS</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>TREATMENT</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', textAlign: 'center' }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVisits.map((visit, index) => {
                const visitId = resolveVisitId(visit) ?? index;
                return (
                  <TableRow key={visitId} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{formatVisitDate(visit.visit_date)}</TableCell>
                    <TableCell>{visit.chief_complaint ?? visit.provlima ?? '—'}</TableCell>
                    <TableCell>{visit.diagnosis ?? visit.eurimata ?? '—'}</TableCell>
                    <TableCell>{visit.treatment_plan ?? visit.plano ?? '—'}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => handlePreview(visit)} color="primary">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton size="small" onClick={() => handlePrint(visit)} color="info">
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(visit)} color="secondary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(visitId)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingVisit ? 'Edit Visit' : 'Add Visit'}</DialogTitle>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, touched, errors, handleBlur, status }) => (
            <Form>
              <DialogContent dividers>
                <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {patientName ? `${patientName} — Visit Summary` : 'Visit Summary'}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Patient Name
                        </Typography>
                        <Typography variant="body1">
                          {formatVisitField(patientName)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Patient ID
                        </Typography>
                        <Typography variant="body1">
                          {formatVisitField(editingVisit?.id_pt ?? patientId)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Visit ID
                        </Typography>
                        <Typography variant="body1">
                          {formatVisitField(editingVisit ? resolveVisitId(editingVisit) : null)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Visit Date
                        </Typography>
                        <Typography variant="body1">
                          {formatVisitDate(values.visit_date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Chief Complaint
                        </Typography>
                        <Typography variant="body1">
                          {formatVisitField(values.chief_complaint)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Diagnosis
                        </Typography>
                        <Typography variant="body1">
                          {formatVisitField(values.diagnosis)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Treatment Plan
                        </Typography>
                        <Typography variant="body1">
                          {formatVisitField(values.treatment_plan)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {formatVisitField(values.notes)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Follow-up
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {formatVisitDate(values.follow_up)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="visit_date"
                      label="Visit Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={values.visit_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.visit_date && Boolean(errors.visit_date)}
                      helperText={touched.visit_date && errors.visit_date}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="follow_up"
                      label="Follow-up Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={values.follow_up}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.follow_up && Boolean(errors.follow_up)}
                      helperText={touched.follow_up && errors.follow_up}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="chief_complaint"
                      label="Chief Complaint"
                      value={values.chief_complaint}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="diagnosis"
                      label="Diagnosis"
                      value={values.diagnosis}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="treatment_plan"
                      label="Treatment Plan"
                      value={values.treatment_plan}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="notes"
                      label="Notes"
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Grid>
                </Grid>
                {status && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {status}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* High-Fidelity Clinical Preview Dialogue */}
      <Dialog 
        open={Boolean(previewVisit)} 
        onClose={() => setPreviewVisit(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#f5f5f5' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Clinical Encounter Preview
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            onClick={() => handlePrint(previewVisit)}
          >
            Print Encounter
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 4 }}>
            <Paper elevation={4} sx={{ mx: 'auto', width: '210mm', minHeight: '297mm' }}>
              <VisitReportView 
                visit={previewVisit} 
                patientName={patientName}
                age={age}
                gender={gender}
                amka={amka}
                dateOfBirth={dateOfBirth}
                phone={phone}
                address={address}
                diagnosis={diagnosis}
              />
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #ddd', p: 2 }}>
          <Button onClick={() => setPreviewVisit(null)} variant="outlined">
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisitsTab;
